import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Row,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckSquareOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { read, utils } from 'xlsx';
import { getContenTypePorTipoDePadron } from 'api/catalogos';
import { getPredios } from 'api/catastro/predio';
import { MAX_CUENTAS } from 'api/procesosMasivos/saldosMasivos';
import { InputSearchPro } from 'components/InputSearch';
import Table from 'components/Table';
import Uploader from 'components/Uploader';
import { getHelpTexts, gettersPadronesCajaMap } from 'utils/padrones';
import padronToString from 'utils/padronesToString';
import { hasAdvanceSearchPermissions, hasPadronPermission } from 'utils/permission/models';
import columnsPrediosOaxaca from 'views/Catastro/columnsPrediosOaxaca';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { padronesEnum } from 'views/Tramites/GPM/enums';

const CUENTA_KEY = 'clave_catastral_municipal';
const PLANTILLA_HEADER = 'Cuenta';
const CUENTA_COMPUESTA_HEADERS = ['Recaudadora', 'Sector', 'Cuenta'];
const PADRONES_EXCLUIDOS = [5];

export const MSG_MAXIMO = `El lote no puede superar ${MAX_CUENTAS.toLocaleString('es-MX')} cuentas por ejecución`;
export const MSG_SIN_PERMISO = 'No cuenta con los permisos para realizar esta acción';
const MSG_FORMATO_INVALIDO = 'El archivo no cumple con el formato esperado, verifique la plantilla de carga';
const MSG_PADRON_SIN_RESULTADOS = 'No se encontró ningún registro en el padrón seleccionado';
const MSG_PADRON_SIN_CONTENT_TYPE = 'No es posible filtrar predios por el padrón seleccionado';

const MSG_SELECCION_INCOMPLETA = 'No fue posible obtener todos los resultados de la búsqueda; la selección no se modificó. Intente de nuevo.';
// Páginas de predios que se piden al mismo tiempo al seleccionar todos
const PAGINAS_EN_PARALELO = 5;

const emptyData = { results: [], count: 0 };

const normalizeCell = (value) => (value === null || value === undefined ? '' : String(value).trim());
const isExcelError = (value) => /^#/.test(value);
// Las cuentas se escriben en el Excel de resultado: un texto que empieza con estos
// caracteres se interpretaría como fórmula
const isFormula = (value) => /^[=+\-@\t\r]/.test(value);
const matchesHeader = (header, expected) => expected
  .every((name, idx) => normalizeCell(header[idx]).toLowerCase() === name.toLowerCase());

/**
 * Cuenta compuesta Recaudadora + Sector + Cuenta (ej. 77 + U + 27689 = 77U27689)
 * @param {Array} row
 * @returns {string|null}
 */
const buildCuentaCompuesta = (row) => {
  const [recaudadora, sector, cuenta] = row.slice(0, 3).map(normalizeCell);
  if (!recaudadora || !sector || !cuenta) return null;
  if ([recaudadora, sector, cuenta].some(isExcelError)) return null;
  return `${recaudadora}${sector.toUpperCase()}${cuenta}`;
};

/**
 * Devuelve las cuentas del archivo o null si no respeta alguno de los layouts aceptados:
 * columnas Recaudadora, Sector y Cuenta, o la plantilla de una sola columna Cuenta.
 * @param {object} workbook
 * @returns {Array<string>|null}
 */
const parseCuentasDeArchivo = (workbook) => {
  const sheet = workbook?.Sheets?.[workbook?.SheetNames?.[0]];
  if (!sheet) return null;
  const rows = utils.sheet_to_json(sheet, {
    header: 1, blankrows: false, defval: null, raw: false,
  });
  if (!rows.length) return null;
  const [header, ...body] = rows;
  let _cuentas;
  if (matchesHeader(header, CUENTA_COMPUESTA_HEADERS)) {
    _cuentas = body.map(buildCuentaCompuesta);
  } else if (matchesHeader(header, [PLANTILLA_HEADER])) {
    const hasExtraColumns = rows.some((row) => row.slice(1).some((cell) => normalizeCell(cell)));
    if (hasExtraColumns) return null;
    _cuentas = body.map((row) => normalizeCell(row[0])).filter((e) => !isExcelError(e));
  } else {
    return null;
  }
  const validas = _cuentas.filter((e) => e);
  if (validas.some(isFormula)) return null;
  const unicas = Array.from(new Set(validas));
  return unicas.length ? unicas : null;
};

/**
 * Estado y acciones para definir un lote de cuentas prediales, por búsqueda en
 * padrón y selección, o por carga de Excel. Lo comparten los procesos masivos.
 * @param {object} params
 * @param {object} params.form - Instancia de Form que también renderiza <LoteDeCuentas />
 * @param {boolean} params.puedeOperar
 */
export const useLoteDeCuentas = ({ form, puedeOperar }) => {
  const archivo = Form.useWatch('archivo', form);
  const tipoDePadronFiltro = Form.useWatch('tipo_de_padron', form);
  const _padrones = useSelector(({ catalogs }) => catalogs.padrones);
  const padronesFiltro = useMemo(() => (_padrones || [])
    .filter((p) => !PADRONES_EXCLUIDOS.includes(p.id)
      && (p.id === padronesEnum.PREDIO
        || (gettersPadronesCajaMap[p.id] && hasPadronPermission(p.id)))), [_padrones]);
  const esPredio = tipoDePadronFiltro === padronesEnum.PREDIO;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(emptyData);
  const [searchParams, setSearchParams] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [filtroCuentas, setFiltroCuentas] = useState('');
  const [archivoCuentas, setArchivoCuentas] = useState(null);
  const [visibleBusquedaPadron, setVisibleBusquedaPadron] = useState(false);
  const [dataExternoPadron, setDataExternoPadron] = useState([]);
  const [padronEncontrado, setPadronEncontrado] = useState([]);

  useEffect(() => {
    if (!puedeOperar || !archivo?.file) {
      setArchivoCuentas(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      let _cuentas = null;
      try {
        const workbook = read(new Uint8Array(event.target.result), { type: 'array' });
        _cuentas = parseCuentasDeArchivo(workbook);
      } catch {
        _cuentas = null;
      }
      if (!_cuentas) {
        message.warning(MSG_FORMATO_INVALIDO);
        form.resetFields(['archivo']);
        setArchivoCuentas(null);
        return;
      }
      if (_cuentas.length > MAX_CUENTAS) message.warning(MSG_MAXIMO);
      setArchivoCuentas(_cuentas);
    };
    reader.onerror = () => {
      message.warning(MSG_FORMATO_INVALIDO);
      form.resetFields(['archivo']);
    };
    reader.readAsArrayBuffer(archivo.file);
  }, [archivo, puedeOperar]);

  const limpiarResultados = () => {
    setData(emptyData);
    setSearchParams(null);
    setCurrentPage(1);
    setSelectedRowKeys([]);
  };

  const limpiar = () => {
    form.resetFields();
    setArchivoCuentas(null);
    setDataExternoPadron([]);
    setPadronEncontrado([]);
    limpiarResultados();
  };

  const fetchCuentas = async (page = 1, params = searchParams) => {
    if (!puedeOperar || !params) return;
    setLoading(true);
    setCurrentPage(page);
    const _data = await getPredios({ ...params, page });
    setData({ results: _data?.results || [], count: _data?.count ?? _data?.results?.length ?? 0 });
    setLoading(false);
  };

  const buscarPredios = async (params) => {
    setSelectedRowKeys([]);
    setSearchParams(params);
    await fetchCuentas(1, params);
  };

  const buscarPrediosDePadron = async (record) => {
    const tipoDePadron = form.getFieldValue('tipo_de_padron');
    if (!puedeOperar || !record?.id || tipoDePadron === padronesEnum.PREDIO
      || PADRONES_EXCLUIDOS.includes(tipoDePadron)) return;
    const contentType = getContenTypePorTipoDePadron(tipoDePadron)
      || padronesFiltro.find((p) => p.id === tipoDePadron)?.content_type;
    if (!contentType) {
      message.warning(MSG_PADRON_SIN_CONTENT_TYPE);
      return;
    }
    setPadronEncontrado([record]);
    form.setFieldsValue({ object_id_padron: record.id, q_padron: null });
    await buscarPredios({ content_object: `${contentType}-${record.id}` });
  };

  const onBuscar = async () => {
    if (!puedeOperar) {
      message.warning(MSG_SIN_PERMISO);
      return;
    }
    if (!esPredio && padronEncontrado.length) {
      await buscarPrediosDePadron(padronEncontrado[0]);
      return;
    }
    let values;
    try {
      values = await form.validateFields(['tipo_de_padron', 'q_padron']);
    } catch {
      return;
    }
    const { tipo_de_padron: tipoDePadron, q_padron: qPadron } = values;
    if (tipoDePadron === padronesEnum.PREDIO) {
      await buscarPredios({ [CUENTA_KEY]: qPadron });
      return;
    }
    const getter = gettersPadronesCajaMap[tipoDePadron];
    if (PADRONES_EXCLUIDOS.includes(tipoDePadron) || !getter) {
      message.warning(MSG_PADRON_SIN_CONTENT_TYPE);
      return;
    }
    setLoading(true);
    const response = await getter({ q: qPadron });
    setLoading(false);
    const encontrados = Array.isArray(response) ? response : response?.results || [];
    if (!encontrados.length) {
      message.warning(MSG_PADRON_SIN_RESULTADOS);
      return;
    }
    if (encontrados.length > 1) {
      setDataExternoPadron(response);
      setVisibleBusquedaPadron(true);
      return;
    }
    await buscarPrediosDePadron(encontrados[0]);
  };

  const onSelectPredioAvanzado = (record) => {
    form.setFieldsValue({ q_padron: record?.[CUENTA_KEY] });
  };

  const limpiarPadronEncontrado = () => {
    form.resetFields(['q_padron', 'object_id_padron']);
    setPadronEncontrado([]);
    setDataExternoPadron([]);
  };

  const agregarCuentas = () => {
    if (!puedeOperar) {
      message.warning(MSG_SIN_PERMISO);
      return;
    }
    const merged = Array.from(new Set([...cuentas, ...selectedRowKeys]));
    if (merged.length > MAX_CUENTAS) {
      message.warning(MSG_MAXIMO);
      return;
    }
    setCuentas(merged);
    setSelectedRowKeys([]);
  };

  /**
   * Selecciona todos los resultados de la búsqueda, de todas las páginas. El endpoint de
   * predios ignora page_size, así que se recorren sus páginas (en grupos, en paralelo).
   * No se permite si los resultados superan el máximo por ejecución, y si no se obtienen
   * todos los registros no se modifica la selección.
   */
  const seleccionarTodos = async () => {
    if (!puedeOperar || !searchParams || !data.count) return;
    if (data.count > MAX_CUENTAS) {
      message.warning(MSG_MAXIMO);
      return;
    }
    setLoading(true);
    const primera = await getPredios({ ...searchParams, page: 1 });
    const registros = [...(primera?.results || [])];
    const porPagina = registros.length;
    const totalDePaginas = porPagina ? Math.ceil(data.count / porPagina) : 0;
    const paginas = Array.from({ length: Math.max(totalDePaginas - 1, 0) }, (_, i) => i + 2);
    for (let i = 0; i < paginas.length; i += PAGINAS_EN_PARALELO) {
      // eslint-disable-next-line no-await-in-loop
      const respuestas = await Promise.all(paginas.slice(i, i + PAGINAS_EN_PARALELO)
        .map((page) => getPredios({ ...searchParams, page })));
      respuestas.forEach((r) => registros.push(...(r?.results || [])));
    }
    setLoading(false);
    if (registros.length < data.count) {
      message.warning(MSG_SELECCION_INCOMPLETA);
      return;
    }
    const keys = registros.map((p) => p?.[CUENTA_KEY]).filter(Boolean);
    setSelectedRowKeys(Array.from(new Set([...selectedRowKeys, ...keys])));
  };

  const quitarCuenta = (cuenta) => {
    if (!puedeOperar) return;
    setCuentas((prev) => prev.filter((c) => c !== cuenta));
  };

  const quitarTodasLasCuentas = () => {
    if (!puedeOperar) return;
    setCuentas([]);
    setFiltroCuentas('');
  };

  const cuentasFiltradas = useMemo(() => {
    const q = filtroCuentas.trim().toUpperCase();
    return q ? cuentas.filter((c) => c.toUpperCase().includes(q)) : cuentas;
  }, [cuentas, filtroCuentas]);

  return {
    form,
    puedeOperar,
    loading,
    data,
    currentPage,
    selectedRowKeys,
    setSelectedRowKeys,
    cuentas,
    cuentasFiltradas,
    filtroCuentas,
    setFiltroCuentas,
    archivoCuentas,
    // El archivo, si se cargó, sustituye a las cuentas agregadas por búsqueda
    cuentasDelLote: archivoCuentas || cuentas,
    tipoDePadronFiltro,
    padronesFiltro,
    esPredio,
    padronEncontrado,
    visibleBusquedaPadron,
    setVisibleBusquedaPadron,
    dataExternoPadron,
    setDataExternoPadron,
    fetchCuentas,
    buscarPrediosDePadron,
    onBuscar,
    onSelectPredioAvanzado,
    limpiarPadronEncontrado,
    agregarCuentas,
    seleccionarTodos,
    quitarCuenta,
    quitarTodasLasCuentas,
    limpiar,
  };
};

const LoteDeCuentas = ({
  lote,
  permission,
  formName,
  initialValues,
  camposExtra,
  permitirQuitarCuentas,
  permitirSeleccionarTodos,
}) => {
  const {
    form,
    puedeOperar,
    tipoDePadronFiltro,
    esPredio,
  } = lote;
  const labelPadronFiltro = esPredio ? 'No. Cuenta' : lote.padronesFiltro
    .find((p) => p.id === tipoDePadronFiltro)?.descripcion || 'Búsqueda en padrón';
  const puedeBusquedaAvanzadaPadron = puedeOperar && !!tipoDePadronFiltro
    && hasAdvanceSearchPermissions(tipoDePadronFiltro);

  const rules = {
    tipoDePadron: [{ required: true, message: 'Seleccione el padrón' }],
    qPadron: [{
      required: true,
      message: esPredio ? 'Capture el número de cuenta' : 'Capture la búsqueda del padrón',
    }],
  };

  const rowSelection = {
    selectedRowKeys: lote.selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (keys) => lote.setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ disabled: !puedeOperar || !record?.[CUENTA_KEY] }),
  };

  const toggleRow = (record) => {
    if (!puedeOperar) return;
    const key = record?.[CUENTA_KEY];
    if (!key) return;
    lote.setSelectedRowKeys((prev) => (prev.includes(key)
      ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const renderCuentas = () => {
    if (!lote.cuentasFiltradas.length) return 'Sin coincidencias';
    if (!permitirQuitarCuentas) return lote.cuentasFiltradas.join(',');
    return lote.cuentasFiltradas.map((cuenta) => (
      <Tag
        key={cuenta}
        closable={puedeOperar}
        onClose={(e) => {
          e.preventDefault();
          lote.quitarCuenta(cuenta);
        }}
        style={{ marginBottom: 5 }}
      >
        {cuenta}
      </Tag>
    ));
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        className="without-feedback"
        name={formName}
        initialValues={initialValues}
        onFinish={lote.onBuscar}
        scrollToFirstError
      >
        <Row gutter={10}>
          {camposExtra}
          <InputSearchPro
            name="q_padron"
            label={labelPadronFiltro}
            tooltip={tipoDePadronFiltro ? getHelpTexts(tipoDePadronFiltro) : null}
            rules={rules.qPadron}
            disabled={!puedeOperar}
            prefix={{
              name: 'tipo_de_padron',
              label: 'Padrón',
              dataSource: lote.padronesFiltro,
              rules: rules.tipoDePadron,
              onChange: lote.limpiarPadronEncontrado,
              allowClear: false,
              style: { width: 200 },
            }}
            onClickAdvanced={puedeBusquedaAvanzadaPadron
              ? () => lote.setVisibleBusquedaPadron(true) : null}
            inputProps={{ onPressEnter: lote.onBuscar }}
            breakPoints={{ xs: 24, sm: 24, md: 12 }}
            resultProps={{
              name: 'object_id_padron',
              label: labelPadronFiltro,
              dataSource: lote.padronEncontrado,
              render: (item) => padronToString(item, tipoDePadronFiltro, false, false),
              onClear: lote.limpiarPadronEncontrado,
              breakPoints: { xs: 24, sm: 24, md: 12 },
            }}
          />
        </Row>
        <Row gutter={10}>
          <Uploader
            title="Archivo"
            formItemName="archivo"
            allowedExtensions={['xlsx']}
            allowPreview={false}
            disabled={!puedeOperar}
          />
        </Row>
        <Row gutter={10} justify="end">
          <Col xs={24} sm={24} md={8} lg={6}>
            <Button block htmlType="submit" icon={<SearchOutlined />} disabled={!puedeOperar}>
              Buscar
            </Button>
          </Col>
        </Row>
      </Form>

      {!!lote.archivoCuentas && (
        <Alert
          style={{ marginTop: 10 }}
          type="info"
          showIcon
          message={`Archivo cargado con ${lote.archivoCuentas.length.toLocaleString('es-MX')} cuenta(s). El proceso usará solo las cuentas del archivo.`}
        />
      )}

      <br />
      <Table
        cols={columnsPrediosOaxaca}
        mobileCols={[columnsPrediosOaxaca[3]]}
        data={lote.data.results}
        rowKey={CUENTA_KEY}
        rowSelection={rowSelection}
        handleOnRowClick={toggleRow}
        allowAdd={false}
        allowEdit={false}
        allowDelete={false}
        allowSearch={false}
        hasAllViewPermission={puedeOperar}
        permission={permission}
        pagination={false}
        count={lote.data.count}
        currentPage={lote.currentPage}
        paginationCallback={(page) => lote.fetchCuentas(page)}
        customActions={[
          permitirSeleccionarTodos && {
            text: `Seleccionar todos${lote.data.count ? ` (${lote.data.count.toLocaleString('es-MX')})` : ''}`,
            icon: <CheckSquareOutlined />,
            onClick: lote.seleccionarTodos,
            hasPermission: puedeOperar,
            disabled: !lote.data.count,
            disabledBySelection: false,
            tooltip: 'Selecciona los resultados de todas las páginas',
          },
          {
            text: 'Agregar',
            icon: <PlusOutlined />,
            onClick: lote.agregarCuentas,
            hasPermission: puedeOperar,
          },
          {
            text: 'Cancelar',
            icon: <CloseOutlined />,
            onClick: () => lote.setSelectedRowKeys([]),
          },
        ].filter(Boolean)}
      />

      {!!lote.cuentas.length && (
        <>
          <br />
          <Row justify="space-between" align="middle" gutter={[10, 10]}>
            <Col>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {`Cuentas (${lote.cuentas.length.toLocaleString('es-MX')})`}
              </Typography.Title>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Row gutter={10} align="middle" wrap={false}>
                <Col flex="auto">
                  <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    value={lote.filtroCuentas}
                    onChange={(e) => lote.setFiltroCuentas(e.target.value)}
                  />
                </Col>
                {permitirQuitarCuentas && (
                  <Col>
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={lote.quitarTodasLasCuentas}
                      disabled={!puedeOperar}
                    >
                      Quitar todas
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Typography.Paragraph style={{ wordBreak: 'break-all', marginTop: 10 }}>
            {renderCuentas()}
          </Typography.Paragraph>
        </>
      )}

      {!!tipoDePadronFiltro && (
        <BusquedaAvanzadaPadrones
          key={tipoDePadronFiltro}
          visible={lote.visibleBusquedaPadron}
          setVisible={lote.setVisibleBusquedaPadron}
          callback={esPredio ? lote.onSelectPredioAvanzado : lote.buscarPrediosDePadron}
          msgAsignarEntidad={esPredio ? lote.onSelectPredioAvanzado : lote.buscarPrediosDePadron}
          tipoDePadron={tipoDePadronFiltro}
          dataExterno={lote.dataExternoPadron}
          setDataExterno={lote.setDataExternoPadron}
          allowAdd={false}
          allowEdit={false}
          allowAddAddress={false}
        />
      )}
    </>
  );
};

LoteDeCuentas.propTypes = {
  // Valor de retorno de useLoteDeCuentas
  lote: PropTypes.shape({
    form: PropTypes.shape({}).isRequired,
    puedeOperar: PropTypes.bool,
    data: PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape({})),
      count: PropTypes.number,
    }).isRequired,
    currentPage: PropTypes.number.isRequired,
    selectedRowKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    setSelectedRowKeys: PropTypes.func.isRequired,
    cuentas: PropTypes.arrayOf(PropTypes.string).isRequired,
    cuentasFiltradas: PropTypes.arrayOf(PropTypes.string).isRequired,
    filtroCuentas: PropTypes.string.isRequired,
    setFiltroCuentas: PropTypes.func.isRequired,
    archivoCuentas: PropTypes.arrayOf(PropTypes.string),
    tipoDePadronFiltro: PropTypes.number,
    padronesFiltro: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    esPredio: PropTypes.bool.isRequired,
    padronEncontrado: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    visibleBusquedaPadron: PropTypes.bool.isRequired,
    setVisibleBusquedaPadron: PropTypes.func.isRequired,
    dataExternoPadron: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({}),
    ]).isRequired,
    setDataExternoPadron: PropTypes.func.isRequired,
    fetchCuentas: PropTypes.func.isRequired,
    buscarPrediosDePadron: PropTypes.func.isRequired,
    onBuscar: PropTypes.func.isRequired,
    onSelectPredioAvanzado: PropTypes.func.isRequired,
    limpiarPadronEncontrado: PropTypes.func.isRequired,
    agregarCuentas: PropTypes.func.isRequired,
    seleccionarTodos: PropTypes.func.isRequired,
    quitarCuenta: PropTypes.func.isRequired,
    quitarTodasLasCuentas: PropTypes.func.isRequired,
  }).isRequired,
  permission: PropTypes.shape({}).isRequired,
  formName: PropTypes.string.isRequired,
  initialValues: PropTypes.shape({}),
  // Campos propios del proceso que se muestran junto a la búsqueda (ej. año y bimestre)
  camposExtra: PropTypes.node,
  // Permite quitar cuentas individuales (o todas) de la lista agregada
  permitirQuitarCuentas: PropTypes.bool,
  // Muestra la acción para seleccionar los resultados de todas las páginas
  permitirSeleccionarTodos: PropTypes.bool,
};

LoteDeCuentas.defaultProps = {
  initialValues: { tipo_de_padron: padronesEnum.PREDIO },
  camposExtra: null,
  permitirQuitarCuentas: false,
  permitirSeleccionarTodos: false,
};

export default LoteDeCuentas;
